import * as React from "react";
import {useState} from "react";
import {Button, Col, Container, Row} from "react-bootstrap";
import {
    getCompatibleLocales,
    Session,
    SessionFinalStates,
    SessionSimpleState,
    SessionState,
    SessionStateMap
} from "../../types/sessionsstate";
import {Routes, useQuery} from "../../utils/router";
import {User} from "../../types/adminstate";
import {formatNumber, isSuperAdmin, transformDataParams} from "../../utils/commons.ts";
import moment from "moment";
import {
    error,
    KCard,
    KColumnAction,
    KModal,
    KModalBackdrop,
    KModalVariant,
    KSelect,
    KTableColumnType,
    KTableFixedSelectionFilter,
    KTableFreeTextFilter,
    KTableGlobalActionType,
    KTableLoader,
    success,
    Translate,
    useKTable,
    Visibility
} from "@kopjra/uikit";
import {
    useChangeOwnerMutation,
    useDeleteReportMutation,
    useDeleteSessionMutation,
    useFailVMSessionMutation,
    useGetSessionsQuery,
    useRefundSessionMutation,
    useStopVMSessionMutation,
    useTerminateVMSessionMutation
} from "../../api/session.ts";
import {useTranslation} from "react-i18next";
import {useGetUserInfo, useLazyGetUsersQuery} from "../../api/user.ts";
import {useAppDispatch} from "../../utils/store.ts";
import {clearReferrerRoute} from "../../slices/global.ts";

const terminateStates: SessionState[] = [
    SessionState.CLOSED,
    SessionState.FATAL_FAILED,
    SessionState.VM_ABORTED_TIMEOUT,
    SessionState.VM_ABORTED_ERROR,
    SessionState.VM_ABORTED_MANUAL,
];

const stopStates: SessionState[] = [
    SessionState.GURU_INTERVENTION
];

const failStates: SessionState[] = [
   SessionState.STARTED,
   SessionState.VM_PREPARING,
   SessionState.VM_READY,
];

/**
 * Generates a user-friendly label for display in dropdowns and lists.
 * If both firstname and lastname are empty, returns just the email.
 * Otherwise, returns "Firstname Lastname (email)" format.
 */
const generateUserLabel = (user: User): string => {
    if (!user.firstname && !user.lastname) {
        return user.email;
    }
    return `${user.firstname || ""} ${user.lastname || ""} (${user.email})`.trim();
};

export const Sessions: React.FC = () => {
    const queryParams: URLSearchParams = useQuery();

    const userId = queryParams.get("userId") || "";
    const userName = queryParams.get("userName");
    const userLastName = queryParams.get("userLastName");
    const userEmail = queryParams.get("userEmail");
    const userCompleteName = `${userName || ""} ${userLastName || ""}`.trim() || userEmail || "";

    const tableName = "admin/sessions/list";

    const {dataParams, Table} = useKTable<Session>({id: tableName});
    const {data: sessions, refetch} = useGetSessionsQuery(transformDataParams(dataParams, {userId}), {pollingInterval: 60000});
    const {data: userInfo} = useGetUserInfo();

    const [triggerGetUsers] = useLazyGetUsersQuery();

    const [triggerRefund] = useRefundSessionMutation();
    const [triggerDeleteReport] = useDeleteReportMutation();
    const [triggerTerminate] = useTerminateVMSessionMutation();
    const [triggerDeleteSession] = useDeleteSessionMutation();
    const [triggerStop] = useStopVMSessionMutation();
    const [triggerChangeOwner] = useChangeOwnerMutation();
    const [triggerFail] = useFailVMSessionMutation();

    const dispatch  = useAppDispatch();

    const { t } = useTranslation();

    const [sessionForChange, setSessionForChange] = useState<Session | undefined>(undefined);
    const [changingOwner, setChangingOwner] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
    const [users, setUsers] = useState<User[] | undefined>(undefined);

    const actions: KColumnAction<Session>[] = [
        {
            label: <><i className="fal fa-eye action-icon"/> <Translate i18nKey="acquisitions.table.actions.view"/></>,
            handler: async (datum: Session) => {dispatch(clearReferrerRoute()); Routes.goA_session(`${datum.id}`);},
            visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.COMPLETED && isSuperAdmin(userInfo),
            tooltip: <Translate i18nKey="acquisitions.table.actions.viewtip"/>,
        },
        {
            label: <><i className="fal fa-money-bill action-icon"/> <Translate i18nKey="admin.sessions.table.actions.refund"/></>,
            handler: async (datum) => { await triggerRefund({sessionId: datum.id}).unwrap(); },
            confirmation: true,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.refundtip"/>,
            visible: (datum: Session) => datum.consumedCredits > 0,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-money-bill-alt action-icon"/> <Translate i18nKey="admin.sessions.table.actions.refundOne"/></>,
            handler: async (datum) => { await triggerRefund({sessionId: datum.id, credits: 1}).unwrap(); },
            confirmation: true,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.refundOneTip"/>,
            visible: (datum: Session) => datum.consumedCredits > 1,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-user-edit action-icon"/> <Translate i18nKey="admin.sessions.table.actions.changeOwner"/></>,
            handler: async (datum) => {
                setSessionForChange(datum);
                (async () => {
                    const userResp = await triggerGetUsers({top: 20, skip: 0}).unwrap();
                    setUsers(userResp.items);
                })().catch((err) => {
                    error(err.message);
                    onHide();
                })
            },
            tooltip: <Translate i18nKey="admin.sessions.table.actions.changeOwnertip"/>,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-file-pdf action-icon"/> <Translate i18nKey="admin.sessions.table.actions.deletePdf"/></>,
            handler: async (datum) => {
                const locales = getCompatibleLocales(datum.pdfTemplateVersion);
                for (const locale of locales) {
                    await triggerDeleteReport({sessionId: datum.id, locale}).unwrap();
                }
            },
            confirmation: true,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.deletePdftip"/>,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-power-off action-icon"/> <Translate i18nKey="admin.sessions.table.actions.terminate"/></>,
            handler: async (datum) => { await triggerTerminate(datum.id).unwrap(); },
            confirmation: true,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.terminatetip"/>,
            visible: (datum) => terminateStates.indexOf(datum.state) !== -1,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-stop action-icon"/> <Translate i18nKey="admin.sessions.table.actions.stop"/></>,
            handler: async (datum) => { await triggerStop(datum.id).unwrap(); },
            confirmation: true,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.stoptip"/>,
            visible: (datum) => stopStates.indexOf(datum.state) !== -1,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-skull-crossbones action-icon"/> <Translate i18nKey="admin.sessions.table.actions.fail"/></>,
            handler: async (datum) => { await triggerFail(datum.id).unwrap(); },
            confirmation: true,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.failtip"/>,
            visible: (datum) => failStates.indexOf(datum.state) !== -1,
            collapsed: true,
        },
        {
            label: <><i className="fal fa-trash action-icon"/> <Translate i18nKey="admin.sessions.table.actions.del"/></>,
            handler: async (datum: Session) => { await triggerDeleteSession(datum.id).unwrap(); },
            confirmation: true,
            visible: (datum: Session) => SessionFinalStates.indexOf(datum.state) !== -1,
            tooltip: <Translate i18nKey="admin.sessions.table.actions.deltip"/>,
        },
    ];

    const globalActions: KTableGlobalActionType<Session>[] = [
        {
            bulk: false,
            handler: async () => { await refetch().catch(console.log); },
            label: <i className="fal fa-sync action-icon"/>,
        },
    ];

    function renderSessionState(datum: object): React.ReactNode {
        const session = datum as Session;
        return <Translate i18nKey={`admin.sessions.stateValues.${session.state}`}/>
    }

    function renderSoftDeleted(datum: object): React.ReactNode {
        const session = datum as Session;
        return session.softDeleted ? <Translate i18nKey={`new.yes`}/> : <Translate i18nKey={`new.no`}/>;
    }

    function renderId(session: Session): React.ReactNode {
        return <span>{session.id} {session.softDeleted ? <i className="text-danger fal fa-times-circle"/> : null}</span>;
    }

    function renderElapsed(datum: object): React.ReactNode {
        const session = datum as Session;
        if (session.start && session.end) {
            const elapsed = moment.duration(moment(session.end).diff(moment(session.start)));
            return `${formatNumber(Math.floor(elapsed.asHours()))}:${formatNumber(elapsed.minutes())}:${formatNumber(elapsed.seconds())}`;
        } else {
            return "";
        }
    }

    const onHide = () => {
        setChangingOwner(false);
        setSessionForChange(undefined);
        setUsers(undefined);
    };

    return (
        <Container style={{marginBottom: 50}}>
            <KModal
                show={!!sessionForChange} closeButton={true} backdrop={KModalBackdrop.static}
                header={<Translate i18nKey="admin.sessions.coModal.title"/>}
                variant={KModalVariant.primary}
                onHide={onHide}
                footer={changingOwner ? (
                    <div style={{display: "block"}} className="text-center"><i className="fal fa-lg fa-circle-notch fa-spin"/></div>
                ) : (
                    <>
                        <Button variant="secondary" onClick={onHide}><Translate i18nKey="admin.sessions.coModal.closeButton"/></Button>
                        <Button variant="primary" type="submit"><Translate i18nKey="admin.sessions.coModal.changeButton"/></Button>
                    </>
                )}
                formStuff={{
                    onSubmit: async () => {
                        setChangingOwner(true);
                        if (sessionForChange && selectedUser) {
                            await triggerChangeOwner({sessionId: sessionForChange.id, newUser: selectedUser}).unwrap();
                        }
                        success(t("admin.sessions.msg.changeOwner"))
                        onHide();
                    }
                }}
            >
                <KSelect
                    id={"ownerSelection"}
                    searchable={true}
                    hint={<Translate i18nKey="admin.sessions.coModal.subtitle"/>}
                    onInputChange={value => {
                        (async () => {
                            if (value && value.length >= 3) {
                                try {
                                    const userResp = await triggerGetUsers({top: 20, skip: 0, email: `~${value}`}).unwrap();
                                    setUsers(userResp.items);
                                } catch (err) {
                                    console.error("Error fetching filtered users", err)
                                }
                            }
                        })();
                    }}
                    options={users ? users.filter((u) => u.userId.toString() !== userId).map((u) => ({label: generateUserLabel(u), value: u})) : []}
                    onChange={value => setSelectedUser(value ? value as User : undefined)}
                />
            </KModal>
            <Row style={{paddingTop: 55}}>
                <Col xs={12}>
                    <KCard header={<><Translate i18nKey="admin.sessions.title"/>{userCompleteName ? ` (${userCompleteName})` : null}</>}>
                        {userId ? (
                            sessions ? (
                                <Row style={{paddingTop: 50}}>
                                    <Col>
                                        <Table
                                            data={sessions}
                                            header={{
                                                filters: [
                                                    new KTableFreeTextFilter("name", t("admin.sessions.table.name"), false),
                                                    new KTableFixedSelectionFilter("state", t("admin.sessions.table.state"), Object.keys(SessionStateMap).map((key) => ({
                                                        label: t(`admin.sessions.stateValues.${key as SessionState}`),
                                                        value: key,
                                                    }))),
                                                    new KTableFixedSelectionFilter("softDeleted", t("admin.sessions.table.softDeleted"),
                                                        [
                                                            {label: t("new.all"), value: "true,false"},
                                                            {label: t("new.yes"), value: "true"},
                                                            {label: t("new.no"), value: "false"},
                                                        ]
                                                    )
                                                ],
                                                globalActions,
                                            }}
                                            footer={{
                                                pager: {
                                                    sizes: [5, 10, 25, 50, 100],
                                                }
                                            }}
                                            body={{
                                                checkboxes: false,
                                                columns: [
                                                    { type: KTableColumnType.GENERIC, label: t("admin.sessions.table.id"), colSpan: 1, alignment: "left", sortField: "id", render: renderId,},
                                                    { type: KTableColumnType.FIELD, field: "name", label: t("admin.sessions.table.name"), colSpan: 2, alignment: "left"},
                                                    { type: KTableColumnType.NUMERIC, field: "consumedCredits", label: t("admin.sessions.table.consumedCredits"), colSpan: 1, alignment: "right"},
                                                    { type: KTableColumnType.DATETIME, field: "created", label: t("admin.sessions.table.created"), colSpan: 2, alignment: "center", justDate: true},
                                                    { type: KTableColumnType.GENERIC, label: t("admin.sessions.table.elapsed"), colSpan: 1, alignment: "center", sortField: "elapsed", render: renderElapsed},
                                                    { type: KTableColumnType.GENERIC, label: t("admin.sessions.table.state"), colSpan: 2, alignment: "right", sortField: "state", render: renderSessionState},
                                                    { type: KTableColumnType.DATETIME, field: "start", label: t("admin.sessions.table.start"), alignment: "center", initialVisibility: Visibility.HIDDEN},
                                                    { type: KTableColumnType.DATETIME, field: "end", label: t("admin.sessions.table.end"), alignment: "center", initialVisibility: Visibility.HIDDEN},
                                                    { type: KTableColumnType.DATETIME, field: "signingDate", label: t("admin.sessions.table.signingDate"), alignment: "center", initialVisibility: Visibility.HIDDEN},
                                                    { type: KTableColumnType.GENERIC, render: renderSoftDeleted, label: t("admin.sessions.table.softDeleted"), alignment: "center", sortField: "softDeleted", initialVisibility: Visibility.HIDDEN},
                                                    { type: KTableColumnType.DATETIME, field: "lastHeartBeat", label: t("admin.sessions.table.lastHeartBeat"), alignment: "center", initialVisibility: Visibility.HIDDEN},
                                                    { type: KTableColumnType.ACTIONS, actions},
                                                ]
                                            }}
                                        />
                                    </Col>
                                </Row>
                            ) : (
                                <Row style={{marginTop: 40, marginBottom: 30}}>
                                    <KTableLoader/>
                                </Row>
                           )
                        ) : (
                            <h4><Translate i18nKey="admin.sessions.missing" style={{color: "#363D49"}}/><span className="btn-link" style={{cursor: "pointer"}} onClick={() => Routes.goAdmin_users()}><Translate i18nKey="admin.sessions.back"/></span></h4>
                        )}
                    </KCard>
                </Col>
            </Row>
        </Container>
    );
};
